import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./floor-detail-image.module.scss";
import {
	FloorEditDataState,
	FloorEditState,
} from "../../../../state/floor/floor-edit-state";
import VelavuButton from "../../../../elements/velavu-button";
import DocumentUploadField from "../../../../elements/document-upload-field";
import VelavuSlider from "../../../../elements/velavu-slider";
import { makeStyleable } from "../../../../helper/icon-helper";
import IconCheckCircleFilled from "../../../../dynamicicons/icon-check-circle-filled";
import VelavuNotice from "../../../../elements/velavu-notice/velavu-notice";
import DocumentUploadPreview from "../../../../elements/document-upload-preview";
import { useUserImageDataURL } from "../../../../data/user-image-data";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../../../elements/velavu-menu";
import InvisibleUploadInput, {
	InvisibleUploadInputRef,
} from "../../../../elements/invisible-upload-input";
import { useVelavuModal } from "../../../../elements/velavu-modal";
import VelavuAlertModalTitle from "../../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuAlertModal from "../../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuSavedIndicator from "../../../../elements/velavu-saved-indicator";

export interface FloorDetailImageProps {
	state: FloorEditState;
}

export default function FloorDetailImage(props: FloorDetailImageProps) {
	const { t } = useTranslation();
	const { pushModal } = useVelavuModal();

	const imageURL = useUserImageDataURL(props.state.image?.renderable);

	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setMenuAnchor(event.currentTarget);
		},
		[setMenuAnchor],
	);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, [setMenuAnchor]);

	const uploadInputRef = useRef<InvisibleUploadInputRef | null>(null);
	const menuTriggerReupload = useCallback(() => {
		closeMenu();
		uploadInputRef.current?.requestUpload();
	}, [closeMenu]);

	const propsRepositionImage = props.state.repositionImage;
	const menuRepositionImage = useCallback(() => {
		closeMenu();
		propsRepositionImage();
	}, [closeMenu, propsRepositionImage]);

	const floorName = props.state.floor?.name;
	const propsRemoveImage = props.state.removeImage;
	const menuRemoveImage = useCallback(async () => {
		closeMenu();

		//Prompt the user if they are sure they want to delete the image
		const result = await pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("dashboard.floor_detail.delete_image.title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					{t("dashboard.floor_detail.delete_image.body", {
						floorName,
					})}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("common.delete")}
						fullWidth
						variant="danger"
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		));

		if (result) {
			propsRemoveImage();
		}
	}, [closeMenu, pushModal, floorName, propsRemoveImage, t]);

	return (
		<>
			<InvisibleUploadInput
				ref={uploadInputRef}
				accept="image/png, image/jpeg, application/pdf"
				onReceive={props.state.replaceImage}
			/>

			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
			>
				<VelavuMenuItem
					label={t("dashboard.floor_detail.reposition")}
					onClick={menuRepositionImage}
				/>
				<VelavuMenuItem
					label={t("dashboard.floor_detail.re_upload")}
					onClick={menuTriggerReupload}
				/>
				<VelavuMenuItem
					label={t("common.delete")}
					danger
					onClick={menuRemoveImage}
				/>
			</VelavuMenu>

			<div className={styles.content}>
				{props.state.image !== null ? (
					<>
						<div>
							<h1 className={styles.header}>
								{t(
									"dashboard.floor_detail.image_section_title",
								)}
							</h1>

							<DocumentUploadPreview
								src={imageURL}
								label={props.state.image.name}
								action={
									<VelavuOverflowButton
										onClick={openMenu}
										open={!!menuAnchor}
									/>
								}
							/>
						</div>

						<div>
							<h3 className={styles.subheader}>
								{t("dashboard.floor_detail.image_opacity")}
							</h3>

							<VelavuSlider
								min={0}
								max={1}
								step={0.25}
								value={props.state.opacity}
								onChange={props.state.setOpacity}
								variant="marked"
							/>
						</div>
					</>
				) : (
					<>
						<VelavuNotice type="info">
							{t("dashboard.floor_create.import_notice")}
						</VelavuNotice>

						<DocumentUploadField onReceive={props.state.addImage} />
					</>
				)}

				{props.state.dataState === FloorEditDataState.Saved ? (
					<VelavuSavedIndicator
						className={styles.primaryButton}
						fullWidth
					/>
				) : (
					<VelavuButton
						className={styles.primaryButton}
						label={t("common.save")}
						disabled={
							props.state.dataState === FloorEditDataState.Idle ||
							props.state.dataState === FloorEditDataState.Saving
						}
						loadingIndicator={
							props.state.dataState === FloorEditDataState.Saving
						}
						fullWidth
						onClick={props.state.submit}
					/>
				)}
			</div>
		</>
	);
}
